import React from "react";
import { Helmet } from "react-helmet-async";
import { physicianJSONLD, clinicJSONLD, loadDoctorData } from "../seo/jsonld";
import reviewsJson from "../data/medical_reviews.json";

interface StructuredDataProps {
  domain?: string;
}

const StructuredData: React.FC<StructuredDataProps> = ({
  domain = "https://mulla-neurospine.co.il",
}) => {
  // Load authoritative doctor/clinic data
  const data = loadDoctorData();

  // Generate authoritative schemas from single source of truth
  const physicianSchema = physicianJSONLD(data);
  const clinicSchema = clinicJSONLD(data);

  // Calculate aggregate rating from reviews data
  const reviews = (reviewsJson as any).reviews || [];
  const ratedReviews = reviews.filter(
    (r: any) => r.rating && parseFloat(r.rating) > 0
  );
  if (ratedReviews.length > 0) {
    const totalRating = ratedReviews.reduce(
      (sum: number, r: any) => sum + parseFloat(r.rating),
      0
    );
    const avgRating = (totalRating / ratedReviews.length).toFixed(1);
    physicianSchema.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: avgRating,
      bestRating: "5",
      worstRating: "1",
      ratingCount: ratedReviews.length,
      reviewCount: ratedReviews.length,
    };
  }

  // WebSite Schema - multilingual names for search discovery
  const webSiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: 'ד"ר האני מולא - מומחה לנוירוכירורגיה ומנתח עמוד שדרה',
    alternateName: [
      "Dr. Hani Mulla - Neurosurgeon & Spine Surgeon",
      "د. هاني مولا - جراح أعصاب وعمود فقري",
      "Dr Hani Mulla - Neurochirurgien & Chirurgien du rachis",
      "Д-р Хани Мулла - Нейрохирург и хирург позвоночника",
    ],
    url: domain,
    description:
      "נוירוכירורג מומחה לניתוחי עמוד שדרה זעיר פולשניים. הזרקות תחת שיקוף, צריבת דיסק וקובלציה ללא אשפוז, PRP לכאבי גב ומפרקים.",
    inLanguage: ["he", "ar", "en", "fr", "ru"],
  };

  // Organization Schema (for the clinic brand) - multilingual
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: data.clinic.name,
    alternateName: [
      data.clinic.name_en,
      (data.clinic as any).name_ar,
      (data.clinic as any).name_fr,
      (data.clinic as any).name_ru,
    ].filter(Boolean),
    url: domain,
    logo: `${domain}/assets/logo.png`,
    description:
      'מרפאה פרטית לניתוחי עמוד שדרה זעיר פולשניים וטיפולי כאב מתקדמים. ד"ר האני מולא - נוירוכירורג מומחה.',
    sameAs: data.doctor.same_as?.length ? data.doctor.same_as : [],
    contactPoint: {
      "@type": "ContactPoint",
      telephone: data.clinic.phone,
      contactType: "customer service",
      areaServed: "IL",
      availableLanguage: data.clinic.available_languages || [
        "Hebrew",
        "Arabic",
        "English",
        "Russian",
      ],
    },
  };

  // MedicalProcedure schemas - multilingual for Google discovery in all 5 languages
  const procedureSchemas = [
    {
      "@context": "https://schema.org",
      "@type": "MedicalProcedure",
      name: "Microdiscectomy",
      alternateName: ["מיקרודיסקטומיה", "استئصال القرص المجهري", "Microdiscectomie", "Микродискэктомия"],
      procedureType: "http://schema.org/SurgicalProcedure",
      description: "Minimally invasive surgery to remove a herniated disc pressing on a nerve, through a small incision with short hospitalization and rapid recovery.",
      bodyLocation: "Lumbar spine",
      howPerformed: "Minimally invasive approach through a small incision using microsurgical techniques under general anesthesia.",
      url: `${domain}/services-dr`,
    },
    {
      "@context": "https://schema.org",
      "@type": "MedicalProcedure",
      name: "Disc Thermocoagulation and Coblation",
      alternateName: ["צריבת דיסק וקובלציה", "كي القرص والتبخير", "Thermocoagulation et coblation discale", "Термокоагуляция и коблация диска"],
      procedureType: "http://schema.org/PercutaneousProcedure",
      description: "Outpatient minimally invasive procedure under sedation using thermal energy to shrink bulging disc tissue and relieve nerve pressure.",
      bodyLocation: "Lumbar spine",
      howPerformed: "Percutaneous approach under fluoroscopic guidance and light sedation, no hospitalization required.",
      url: `${domain}/services-dr`,
    },
    {
      "@context": "https://schema.org",
      "@type": "MedicalProcedure",
      name: "Fluoroscopy-Guided Spinal Injections",
      alternateName: ["הזרקות תחת שיקוף", "حقن تحت التنظير الفلوروسكوبي", "Injections rachidiennes guidées par fluoroscopie", "Инъекции под контролем флюороскопии"],
      procedureType: "http://schema.org/PercutaneousProcedure",
      description: "Precision pain relief injections including epidural, nerve root blocks, facet joint and sacroiliac joint injections, all guided by real-time fluoroscopy imaging.",
      bodyLocation: "Cervical, thoracic and lumbar spine",
      howPerformed: "Outpatient procedure under fluoroscopic guidance for precise needle placement.",
      url: `${domain}/services-dr`,
    },
    {
      "@context": "https://schema.org",
      "@type": "MedicalProcedure",
      name: "PRP Injections for Back and Joint Pain",
      alternateName: ["הזרקות PRP לכאבי גב ומפרקים", "حقن PRP لآلام الظهر والمفاصل", "Injections de PRP pour douleurs dorsales et articulaires", "PRP-инъекции при болях в спине и суставах"],
      procedureType: "http://schema.org/PercutaneousProcedure",
      description: "Platelet-Rich Plasma injections using the patient's own blood components to promote natural healing of back and joint pain without surgery.",
      bodyLocation: "Spine and joints",
      howPerformed: "Outpatient injection of concentrated platelets from patient's own blood into the affected area.",
      url: `${domain}/services-dr`,
    },
    {
      "@context": "https://schema.org",
      "@type": "MedicalProcedure",
      name: "Endoscopic Spine Surgery",
      alternateName: ["אנדוסקופיה לעמוד שדרה", "جراحة بالمنظار للعمود الفقري", "Chirurgie endoscopique du rachis", "Эндоскопическая хирургия позвоночника"],
      procedureType: "http://schema.org/SurgicalProcedure",
      description: "Advanced minimally invasive spine surgery using an endoscope for direct visualization, allowing precise treatment through very small incisions.",
      bodyLocation: "Cervical and lumbar spine",
      howPerformed: "Small-incision approach using endoscopic camera and microsurgical instruments.",
      url: `${domain}/services-dr`,
    },
    {
      "@context": "https://schema.org",
      "@type": "MedicalProcedure",
      name: "Spinal Fixation and Stabilization",
      alternateName: ["קיבוע עמוד שדרה", "تثبيت العمود الفقري", "Fixation et stabilisation vertébrale", "Фиксация и стабилизация позвоночника"],
      procedureType: "http://schema.org/SurgicalProcedure",
      description: "Surgical procedure to restore stability to the spine using screws, rods or cages, combining open and minimally invasive approaches based on injury severity.",
      bodyLocation: "Cervical, thoracic and lumbar spine",
      howPerformed: "Open or minimally invasive approach with pedicle screws and rods for structural stabilization.",
      url: `${domain}/services-dr`,
    },
  ];

  return (
    <Helmet>
      {/* WebSite Schema */}
      <script type="application/ld+json">
        {JSON.stringify(webSiteSchema)}
      </script>

      {/* Organization Schema */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>

      {/* Authoritative Physician Schema (from CV/Profile data) */}
      <script type="application/ld+json">
        {JSON.stringify(physicianSchema)}
      </script>

      {/* Authoritative MedicalClinic Schema (from CV/Profile data) */}
      <script type="application/ld+json">{JSON.stringify(clinicSchema)}</script>

      {/* MedicalProcedure Schemas - multilingual for search discovery */}
      {procedureSchemas.map((schema, i) => (
        <script key={`proc-${i}`} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default StructuredData;
